import { useEffect, useState, useCallback } from "react";
import API from "@/lib/api";
import { Loader2, Search, RefreshCw, ChevronLeft, ChevronRight, X, ScrollText } from "lucide-react";

const KINDS = [
  { value: "", label: "All" },
  { value: "http", label: "API Requests" },
  { value: "integration", label: "Integrations (ERP/Zoho)" },
];

const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
    });
  } catch { return iso; }
};

const statusColor = (s) => {
  if (s == null) return "bg-gray-100 text-gray-500";
  if (s >= 500) return "bg-red-100 text-red-700";
  if (s >= 400) return "bg-amber-100 text-amber-700";
  if (s >= 200 && s < 300) return "bg-emerald-100 text-emerald-700";
  return "bg-gray-100 text-gray-600";
};

export default function AdminApiLogs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [kind, setKind] = useState("");
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: "50" });
      if (kind) params.append("kind", kind);
      if (errorsOnly) params.append("errors_only", "true");
      if (search.trim()) params.append("search", search.trim());
      const { data } = await API.get(`/admin/api-logs?${params.toString()}`);
      setLogs(data.logs || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch { setLogs([]); } finally { setLoading(false); }
  }, [page, kind, errorsOnly, search]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5" data-testid="admin-api-logs">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <ScrollText className="w-6 h-6 text-[#008A99]" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">API Logs</h1>
            <p className="text-sm text-gray-500">All requests & 3rd-party integration calls with timestamps ({total} records)</p>
          </div>
        </div>
        <button onClick={load} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white border text-sm font-medium text-gray-600 hover:bg-gray-50" data-testid="api-logs-refresh">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap bg-white rounded-xl border p-3">
        <div className="flex items-center gap-1">
          {KINDS.map((k) => (
            <button key={k.value} onClick={() => { setPage(1); setKind(k.value); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${kind === k.value ? "bg-[#008A99] text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}
              data-testid={`api-logs-kind-${k.value || "all"}`}>{k.label}</button>
          ))}
        </div>
        <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 ml-2 cursor-pointer">
          <input type="checkbox" checked={errorsOnly} onChange={(e) => { setPage(1); setErrorsOnly(e.target.checked); }} className="accent-[#008A99]" data-testid="api-logs-errors-only" />
          Errors only (4xx/5xx)
        </label>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { setPage(1); load(); } }}
            placeholder="Search path / action / system..." className="w-full pl-9 pr-3 py-2 border rounded-xl text-sm focus:outline-none focus:border-[#008A99]" data-testid="api-logs-search" />
        </div>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#008A99]" /></div>
      ) : logs.length === 0 ? (
        <div className="py-16 text-center text-sm text-gray-500" data-testid="api-logs-empty">No logs found.</div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-400">
                <tr>
                  <th className="text-left px-4 py-2.5 font-semibold">Time</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Type</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Endpoint / Action</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                  <th className="text-right px-4 py-2.5 font-semibold">Duration</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {logs.map((l) => (
                  <tr key={l.id} onClick={() => setSelected(l)} className="hover:bg-[#E0F4F5]/30 cursor-pointer" data-testid="api-log-row">
                    <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap text-xs">{fmtTime(l.timestamp)}</td>
                    <td className="px-4 py-2.5">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${l.kind === "integration" ? "bg-indigo-100 text-indigo-700" : "bg-sky-100 text-sky-700"}`}>
                        {l.kind === "integration" ? (l.system || "INTEGRATION") : (l.method || "HTTP")}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-gray-800 font-medium max-w-[420px] truncate">
                      {l.kind === "integration" ? (l.action || l.url) : l.path}
                      {l.query ? <span className="text-gray-400">?{l.query}</span> : null}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-md ${statusColor(l.status)}`}>{l.status ?? "—"}</span>
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-500 text-xs whitespace-nowrap">{l.duration_ms != null ? `${l.duration_ms} ms` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="p-2 rounded-lg border bg-white disabled:opacity-40" data-testid="api-logs-prev"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm text-gray-500">Page {page} of {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage((p) => Math.min(pages, p + 1))} className="p-2 rounded-lg border bg-white disabled:opacity-40" data-testid="api-logs-next"><ChevronRight className="w-4 h-4" /></button>
        </div>
      )}

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-[120] bg-black/40 flex items-center justify-center p-4" onClick={() => setSelected(null)} data-testid="api-log-detail">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b sticky top-0 bg-white">
              <h3 className="font-bold text-gray-900">Log Detail</h3>
              <button onClick={() => setSelected(null)} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid="api-log-detail-close"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <Field label="Time" value={fmtTime(selected.timestamp)} />
              <Field label="Type" value={selected.kind} />
              {selected.kind === "integration" ? (
                <>
                  <Field label="System" value={selected.system} />
                  <Field label="Action" value={selected.action} />
                  <Field label="URL" value={selected.url} />
                </>
              ) : (
                <>
                  <Field label="Method" value={selected.method} />
                  <Field label="Path" value={selected.path + (selected.query ? `?${selected.query}` : "")} />
                  <Field label="User ID" value={selected.user_id || "—"} />
                  <Field label="IP" value={selected.ip || "—"} />
                </>
              )}
              <Field label="Status" value={String(selected.status ?? "—")} />
              <Field label="Duration" value={selected.duration_ms != null ? `${selected.duration_ms} ms` : "—"} />
              {selected.error && <Field label="Error" value={selected.error} error />}
              {selected.request && <Block label="Request" value={selected.request} />}
              {selected.response && <Block label="Response" value={selected.response} />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, value, error }) => (
  <div className="flex gap-3">
    <span className="text-gray-400 text-xs uppercase tracking-wider w-24 flex-shrink-0 pt-0.5">{label}</span>
    <span className={`font-medium break-all ${error ? "text-red-600" : "text-gray-800"}`}>{value}</span>
  </div>
);

const Block = ({ label, value }) => (
  <div>
    <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">{label}</p>
    <pre className="bg-gray-50 border rounded-lg p-3 text-[11px] text-gray-700 whitespace-pre-wrap break-all overflow-x-auto max-h-64">{value}</pre>
  </div>
);
